/**
 * The booking card's second action — "Ask a Question", under the amber CTA.
 *
 * It exists as its own file because two cards render it — the availability card
 * and the on-request card — and the treatment below has to stay identical in
 * both.
 *
 * `_not-configured-card` deliberately does NOT use this. There, asking is the
 * only action on the card rather than the alternative to booking, so it is the
 * primary and carries the tenant's colour.
 *
 * WHAT THE TREATMENT IS:
 *
 * `variant="outline"` is the DESKTOP treatment and is left exactly as it was —
 * a brand border with a brand label. An earlier pass changed the variant itself
 * to `ghost` and rebuilt the outline in `sm:` classes, which quietly repainted
 * the 1440px booking rail: the brand border and brand label became a neutral
 * grey box. The phone override is additive now, so desktop cannot drift again.
 *
 * BELOW `lg` it becomes the mobile guide's `.btn-ghost` — a FILLED chip on
 * `--chip`, with no border. The reason is a phone reason: on a 358px card two
 * full-width outlined boxes stacked one under the other read as a pair of
 * equals, and the amber above needs the quieter thing below to read as its
 * alternative rather than its twin.
 *
 * `max-lg`, not `max-sm`. Below 1024px this card is not a rail beside the page
 * — it sits inline inside "Dates & prices", which is the mobile layout, and an
 * 800px touch tablet is on that layout too. While this said `sm` the phone fill
 * stopped at 640px and a tablet got the desktop hover treatment on a screen
 * with no hover. `lg` is the one number this route changes layout at; see
 * `_use-is-desktop.client.ts`.
 *
 * `packages/ui/CLAUDE.md` §2.3 allows a class-level treatment like this only
 * when it is scoped to one surface — this is one page, below one breakpoint.
 */
import type { ReactNode } from "react";

import { Button } from "@tripcart/traveler-ui/components/button";

import styles from "./_ask-button.module.css";

interface AskButtonProps {
  onClick: () => void;
  /** Prefetch hooks for the dialog chunk. Both are passed through unchanged so
   *  the click still feels instant — see `prefetchQuestionDialog`. */
  onMouseEnter?: () => void;
  onFocus?: () => void;
  children: ReactNode;
}

export function AskButton({
  onClick,
  onMouseEnter,
  onFocus,
  children,
}: AskButtonProps) {
  return (
    <Button
      variant="outline"
      size="lg"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      className={styles.askButton}
    >
      {children}
    </Button>
  );
}
